import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MapPin, Calendar, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DisplayCardImage } from '@/components/ui/ResponsiveImage';
import { DisplayWithOwner } from '@/data/mockDisplaysData';
import { formatSchedule } from '@/utils/displayUtils';
import { useFavoritesStore } from '@/stores/useFavoritesStore';
import { useAuth } from '@/contexts/AuthContext';
import { Favoritable } from '@/types';

interface DisplayCardProps {
  display: DisplayWithOwner;
  onClick?: () => void;
}

const DisplayCard: React.FC<DisplayCardProps> = ({ display, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { isAuthenticated } = useAuth();
  const { addToFavorites, removeFromFavorites, isFavorite: isFavoriteStore } = useFavoritesStore();

  const formattedSchedule = formatSchedule(display.schedule);
  
  // Convert display to Favoritable format
  const displayAsFavoritable: Favoritable = {
    id: display.id.toString(), // Convert number to string for consistency
    type: 'display',
    title: display.name,
    imageUrl: display.image_url || '',
    rating: display.rating,
    location: display.location
  };
  
  const isFavorite = isFavoriteStore(displayAsFavoritable.id);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAuthenticated) {
      if (isFavorite) {
        removeFromFavorites(displayAsFavoritable.id);
      } else {
        addToFavorites(displayAsFavoritable);
      }
    }
  };


  return (
    <div
      id={`display-${display.id}`}
      className={cn(
        "group relative rounded-xl overflow-hidden bg-card transition-all duration-300 shadow-xs hover:shadow-md",
        onClick && "cursor-pointer"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        // Don't trigger if clicking on interactive elements
        if ((e.target as HTMLElement).closest('button, a')) return;
        onClick?.();
      }}
    >
      <div className="aspect-4/3 w-full overflow-hidden relative">
        <DisplayCardImage
          displayId={display.id.toString()}
          imagePath={display.image_url || ''}
          alt={display.name}
          className={cn(
            "w-full h-full object-cover transition-all duration-700 ease-out",
            isHovered ? "scale-110" : "scale-100"
          )}
          onLoad={() => setIsLoaded(true)}
          fallbackSrc={display.image_url}
        />
        
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "absolute top-3 right-3 z-20 rounded-full bg-black/20 backdrop-blur-xs hover:bg-black/30 text-white",
            !isAuthenticated && "opacity-50 cursor-not-allowed"
          )}
          onClick={handleFavoriteClick}
          title={isAuthenticated ? "Toggle favorite" : "Please login to favorite"}
        >
          <Heart 
            size={18} 
            className={cn(
              "transition-all",
              isFavorite ? "fill-destructive stroke-destructive" : "fill-none"
            )} 
          />
        </Button>
        
        <Badge 
          variant="secondary" 
          className="absolute bottom-3 left-3 z-20 bg-black/30 text-white backdrop-blur-xs border-none"
        >
          {display.display_type}
        </Badge>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex justify-between items-start">
          <h3 className="font-medium text-lg line-clamp-2">{display.name}</h3>
        </div>

        {display.owner && display.owner.name && display.owner.name !== 'Unknown Owner' && (
          <div className="flex items-center gap-2">
            <Link 
              to={`/owner/${display.id}`} 
              className="flex items-center gap-2 hover:text-primary transition-colors" 
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar className="h-6 w-6">
                <AvatarImage src={display.owner.avatar} alt={display.owner.name} />
                <AvatarFallback>{display.owner.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {display.owner.name}
              </span>
            </Link>
          </div>
        )}

        <div className="text-sm text-muted-foreground space-y-2">
          <div className="flex items-center gap-1.5">
            <MapPin size={14} />
            <span className="line-clamp-1">{display.location}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar size={14} />
            <span className="line-clamp-1">{formattedSchedule}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Music size={14} />
            <span>{display.songCount} songs</span>
          </div>
        </div>


      </div>
      
      <Link 
        to={`/display/${display.id}`} 
        className="absolute inset-0 z-10" 
        aria-label={`View ${display.name}`}
      ></Link>

    </div>
  );
};

export default DisplayCard;
